import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ShieldCheck, ShieldAlert, ExternalLink, PlayCircle, Loader2, Send, BarChart3, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";

const DNS_STEPS = [
  {
    title: "Buy or pick a domain",
    body: "You'll need a domain you own — e.g. theblkcommunity.com. Any registrar works (Namecheap, Cloudflare, Google Domains, etc.).",
  },
  {
    title: "Add the domain in Resend",
    body: "Open the Resend dashboard, go to Domains → Add Domain, and type your domain.",
    href: "https://resend.com/domains",
    cta: "Open Resend Domains",
  },
  {
    title: "Add Resend's DNS records at your registrar",
    body: "Resend will show you 3 DNS records (SPF TXT, DKIM TXT, MX). Copy them into your registrar's DNS panel exactly as shown.",
  },
  {
    title: "Click 'Verify' in Resend",
    body: "DNS usually propagates within minutes. Once Resend shows the domain as Verified, you're cleared to send from any address on that domain.",
  },
  {
    title: "Update SENDER_EMAIL",
    body: "In /app/backend/.env, set SENDER_EMAIL=hello@yourdomain.com (or whichever address you prefer). Restart the backend.",
  },
];

export default function AdminEmailTab() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusinessId, setPreviewBusinessId] = useState("");
  const [previewSending, setPreviewSending] = useState(false);

  const sendOwnerTrafficPreview = async () => {
    setPreviewSending(true);
    try {
      const res = await axios.post(
        `${API}/digest/owner-traffic-preview`,
        {},
        {
          withCredentials: true,
          params: previewBusinessId ? { business_id: previewBusinessId } : {},
        },
      );
      toast.success(
        `Preview sent to ${res.data.recipient}`,
        { description: res.data.business_name ? `Rendered for ${res.data.business_name}` : undefined },
      );
      setPreviewOpen(false);
      setPreviewBusinessId("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview send failed");
    } finally {
      setPreviewSending(false);
    }
  };

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/digest/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load digest stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const runDigest = async (dry_run) => {
    setRunning(true);
    try {
      const res = await axios.post(
        `${API}/digest/run-now?dry_run=${dry_run}`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `${dry_run ? "Dry-run" : "Digest sent"} — ${res.data?.sent ?? 0} delivered, ${res.data?.skipped ?? 0} skipped`
      );
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const sender = stats?.sender_email || "onboarding@resend.dev";
  const isSandbox = sender.endsWith("@resend.dev");
  const configured = !!stats?.resend_configured;

  return (
    <div className="space-y-6" data-testid="admin-email-tab">
      {/* Status */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="font-heading text-xl text-tbc-navy">Email Status</h2>
          <p className="text-sm text-gray-500 mt-1">
            Sender, subscriber count, and recent digest activity.
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-gray-500">Sender</p>
            <p className="font-mono text-sm text-tbc-navy mt-1" data-testid="email-sender">{sender}</p>
            {isSandbox ? (
              <span className="inline-flex items-center gap-1 mt-2 text-[11px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                <ShieldAlert className="w-3 h-3" /> Sandbox — verify a domain
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 mt-2 text-[11px] text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                <ShieldCheck className="w-3 h-3" /> Custom domain
              </span>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-gray-500">Resend SDK</p>
            <p className="text-sm text-tbc-navy mt-1">
              {configured ? "Configured" : "Not configured"}
            </p>
            <span
              className={`inline-flex items-center gap-1 mt-2 text-[11px] px-2 py-0.5 rounded-full ${
                configured ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
              }`}
            >
              {configured ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
              {configured ? "RESEND_API_KEY set" : "RESEND_API_KEY missing"}
            </span>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-gray-500">Digest subscribers</p>
            <p className="font-heading text-2xl text-tbc-navy mt-1" data-testid="digest-subscriber-count">
              {loading ? "—" : stats?.subscribers ?? 0}
            </p>
            <p className="text-[11px] text-gray-500 mt-1">Opted in via Profile</p>
          </div>
        </div>
      </div>

      {/* Manual digest trigger */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="font-heading text-xl text-tbc-navy">Weekly Digest</h2>
          <p className="text-sm text-gray-500 mt-1">
            Auto-runs every Monday at 09:00 UTC. Send manually or dry-run to preview the impact.
          </p>
        </div>
        <div className="p-6 flex flex-wrap gap-3 items-center">
          <Button
            variant="outline"
            onClick={() => runDigest(true)}
            disabled={running}
            data-testid="digest-dry-run-btn"
          >
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
            Dry run (count only)
          </Button>
          <Button
            onClick={() => {
              if (window.confirm(`Send digest to ${stats?.subscribers ?? 0} subscriber(s)?`)) {
                runDigest(false);
              }
            }}
            disabled={running || !configured || (stats?.subscribers ?? 0) === 0}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="digest-send-now-btn"
          >
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send digest now
          </Button>
        </div>

        {stats?.recent_runs?.length > 0 && (
          <div className="px-6 pb-6">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Recent runs</p>
            <div className="border rounded-lg divide-y">
              {stats.recent_runs.map((r, idx) => (
                <div key={r.ran_at || `run-${idx}`} className="px-4 py-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {new Date(r.ran_at).toLocaleString()}{" "}
                    {r.dry_run && <span className="text-xs text-gray-400">· dry-run</span>}
                  </span>
                  <span className="text-tbc-navy">{r.sent} sent · {r.skipped} skipped</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Weekly traffic email preview (QA only, isolated from production send) */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-owner-traffic-preview-card">
        <div className="p-6 border-b">
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-tbc-navy" /> Weekly Traffic Email — Preview
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Send yourself the exact Monday traffic report a business owner would receive.
            The preview uses the real production template and does <strong>not</strong> touch the scheduled Monday send.
          </p>
        </div>
        <div className="p-6 space-y-3">
          <div className="rounded-lg bg-tbc-cream/40 border border-tbc-navy/10 p-3 text-xs text-tbc-navy/80 leading-relaxed">
            <p><strong>Test recipient:</strong> {user?.email || "your admin email on file"}</p>
            <p className="mt-1 text-tbc-navy/60">
              Only this address will receive the preview. Business owners will not.
            </p>
          </div>
          <Button
            onClick={() => setPreviewOpen(true)}
            className="rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white"
            data-testid="preview-weekly-traffic-btn"
          >
            <Eye className="w-4 h-4 mr-2" /> Preview Weekly Traffic Email
          </Button>
        </div>
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
          role="dialog"
          aria-modal="true"
          data-testid="preview-weekly-traffic-modal"
        >
          <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden">
            <div className="bg-tbc-navy text-white p-5">
              <p className="text-xs uppercase tracking-wider text-tbc-gold font-semibold">TEST · Preview</p>
              <h3 className="font-heading text-2xl mt-1">Send Test Weekly Report</h3>
              <p className="text-white/70 text-xs mt-2">
                Will send only to <strong>{user?.email}</strong>. Scheduled Monday send is unaffected.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-semibold text-tbc-navy/60 uppercase tracking-wider">
                  Business ID (optional)
                </label>
                <input
                  type="text"
                  value={previewBusinessId}
                  onChange={(e) => setPreviewBusinessId(e.target.value)}
                  placeholder="Leave blank for top-traffic business"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy font-mono"
                  data-testid="preview-business-id-input"
                />
                <p className="mt-1 text-[11px] text-tbc-navy/50">
                  If empty, the preview picks the business with the most all-time Blinterest saves.
                </p>
              </div>
            </div>
            <div className="border-t p-4 flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => { setPreviewOpen(false); setPreviewBusinessId(""); }}
                disabled={previewSending}
                className="w-full sm:w-auto rounded-full"
                data-testid="preview-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={sendOwnerTrafficPreview}
                disabled={previewSending}
                className="w-full sm:flex-1 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
                data-testid="preview-send-btn"
              >
                {previewSending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Preview to {user?.email}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DNS / domain instructions */}
      {isSandbox && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border-2 border-orange-200">
          <div className="p-6 border-b bg-orange-50">
            <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-600" /> Verify a sending domain
            </h2>
            <p className="text-sm text-gray-700 mt-1">
              You&apos;re currently on Resend&apos;s sandbox sender. It only delivers to addresses you&apos;ve
              personally verified inside Resend. To send to <em>any</em> recipient (including buyers and sellers
              on this platform), follow these steps:
            </p>
          </div>
          <ol className="p-6 space-y-4">
            {DNS_STEPS.map((step, i) => (
              <li key={step.title || `dns-step-${i}`} className="flex gap-4">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-tbc-navy text-white text-sm font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <div>
                  <p className="font-medium text-tbc-navy">{step.title}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{step.body}</p>
                  {step.href && (
                    <a
                      href={step.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-tbc-navy hover:text-tbc-gold mt-1"
                    >
                      {step.cta} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
